import dayjs from 'dayjs'
import { MonthlyAgentBreakdown, MonthlyBreakdownByCustomerType } from '@boltenergy-be/shared'
import { NestedDataTableRow } from '../components/nested-data-table/types.ts'
import { formatAmount } from 'utils/format.ts'
import { SalesDashboardChartEntry } from '../components/sales-dashboard-chart/types.ts'

/**
 * Mapping utility function from API data to chart representation data
 * @param {MonthlyBreakdownByCustomerType[]} data
 * @returns {SalesDashboardChartEntry[]}
 */
export const mapMonthBreakdownToSalesDashboardChartEntry = (data?: MonthlyBreakdownByCustomerType[]): SalesDashboardChartEntry[] => {
  if (!data) return []

  return data.map(
    ({ month, ...values }) =>
      ({
        label: dayjs(month).format('MMM. YYYY'),
        hoverLabel: dayjs(month).format('MMMM YYYY'),
        values
      }) satisfies SalesDashboardChartEntry
  )
}

/**
 * Mapping utility function from API data to table rows, one row per month (newest first) broken down per agent
 * @param {MonthlyAgentBreakdown} data
 * @param {'count' | 'rate'} metricKey
 * @returns {NestedDataTableRow[]}
 */
export const mapAgentBreakdownToSalesTableEntry = (
  data: MonthlyAgentBreakdown | undefined,
  metricKey: 'count' | 'rate'
): NestedDataTableRow[] => {
  if (!data) return []

  const formatMetric = (value: number): string => {
    const formatted = formatAmount(value, { fractionDigits: metricKey === 'rate' ? 2 : 0 })
    return metricKey === 'rate' ? `${formatted}%` : formatted
  }

  return Object.entries(data)
    .sort(([a], [b]) => dayjs(b).valueOf() - dayjs(a).valueOf())
    .map(([month, { total, agents }]) => {
      const id = dayjs(month).format('YYYY-MM')

      return {
        id,
        cells: {
          date: dayjs(month).format('MMM. YYYY'),
          agent: '',
          [metricKey]: formatMetric(total)
        },
        children: agents.map(({ agentId, agentName, metric }) => ({
          id: `${id}-${agentId}`,
          cells: {
            date: '',
            agent: agentName,
            [metricKey]: formatMetric(metric)
          }
        }))
      }
    })
}

/**
 * Checks whether a report has no data or only zero values, in which case there is nothing to export
 * @param {MonthlyBreakdownByCustomerType[] | MonthlyAgentBreakdown} data
 * @returns {boolean}
 */
export const isReportEmpty = (data: MonthlyBreakdownByCustomerType[] | MonthlyAgentBreakdown | undefined): boolean => {
  if (!data) return true

  if (Array.isArray(data)) {
    return data.every(({ month: _month, ...values }) => Object.values(values).every((value) => !value))
  }

  return Object.values(data).every(({ total, agents }) => !total && agents.every(({ metric }) => !metric))
}
